import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { IContentHeader } from "../../../layout/components/content-header/content-header.component";
import { UtilsService } from "../../../auth/service/utils.service";
import moment from "moment";
import { HttpClient } from "@angular/common/http";
import { tap } from "rxjs/operators";

import { ApexChart, ApexDataLabels, ApexFill, ApexLegend, ApexPlotOptions, ApexStroke, ApexXAxis, ApexYAxis, ChartComponent } from "ng-apexcharts";
import { BehaviorSubject } from "rxjs";
import { ActivatedRoute } from "@angular/router";

export type ChartOptions = {
  series: any;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  stroke: ApexStroke;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  colors: string[];
  fill: ApexFill;
  legend: ApexLegend;
};

interface monthBalance {
  month: number;
  year: number;
  total_commission_real_cost: number;
  total_provider_cost: number;
  total_structure_cost: number;
  total_profit: number;
  total_cost: number;
  margin_currency: number;
  margin_percentage: number;
  fake?: boolean;
}

@Component({
  selector: "app-economics-balance",
  templateUrl: "./economics-balance.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [``]
})
export class EconomicsBalanceComponent implements OnInit {
  @ViewChild("chart") chart: ChartComponent;
  chartOptions: Partial<ChartOptions>;

  contentHeader: IContentHeader = {
    headerTitle: "MENU.ECONOMIC_BALANCE",
    actionButton: false,
    breadcrumb: {
      type: "",
      links: [
        {
          name: "Home",
          isLink: true,
          link: "/"
        },
        {
          name: "MENU.ECONOMICS_DATA",
          isLink: false
        },
        {
          name: "MENU.ECONOMIC_BALANCE",
          isLink: false
        }
      ]
    }
  };

  balance: BehaviorSubject<monthBalance[]> = new BehaviorSubject([]);
  total_row: Partial<monthBalance> = {};
  showChart: boolean = false;

  filterMonthFrom: number = 1;
  filterYearFrom: number = moment().year();
  filterMonthTo: number = moment().month() + 1;
  filterYearTo: number = moment().year();

  yearsList: number[] = Array.from({ length: this.filterYearFrom - 2023 + 2 }, (v, i) => 2023 + i);
  monthsList: string[] = Array.apply(0, Array(12)).map((_, i) => this._utilService.ucFirst(moment().month(i).format("MMMM")));

  chartData: any = [];
  translatedKeys: string[] = [];
  openedYear: number[] = [];

  constructor(public _utilService: UtilsService, private http: HttpClient, private _changeDetectorRef: ChangeDetectorRef,
              private route: ActivatedRoute) {
    this._utilService.disableAutoHideLoading();

    this.route.queryParams.subscribe(params => {
      if (params.start_date) {
        const start_date = moment(params.start_date);
        this.filterMonthFrom = start_date.month() + 1;
        this.filterYearFrom = start_date.year();
      }
      if (params.end_date) {
        const end_date = moment(params.end_date);
        this.filterMonthTo = end_date.month() + 1;
        this.filterYearTo = end_date.year();
      }
    });


    this.chartOptions = {
      series: [],
      chart: {
        type: "bar",
        height: 350,
        stacked: true
      },
      stroke: {
        width: 1,
        colors: ["#fff"]
      },
      dataLabels: {
        formatter: (val) => Math.round(Number(val) / 1000) + "K"
      },
      plotOptions: {
        bar: {
          horizontal: false
        }
      },
      xaxis: {
        categories: this.monthsList
      },
      fill: {
        opacity: 1
      },
      colors: ["#00E396", "#008FFB", "#5ADAC6", "#F2A115"],
      yaxis: {
        labels: {
          formatter: (val) => this._utilService.addDotsToNumber(val) + "€"
        }
      },
      legend: {
        position: "top",
        horizontalAlign: "left"
      }
    };
  }

  async ngOnInit() {
    this.loadBalance();
    await this._utilService.translate("TOTAL_PROFIT").then((result: any) => this.translatedKeys.push(result));
    await this._utilService.translate("TOTAL_COMMISSION_REAL_COST").then((result: any) => this.translatedKeys.push(result));
    await this._utilService.translate("TOTAL_PROVIDER_COST").then((result: any) => this.translatedKeys.push(result));
    await this._utilService.translate("TOTAL_STRUCTURE_COST").then((result: any) => this.translatedKeys.push(result));
    await this._utilService.translate("TOTAL_COST").then((result: any) => this.translatedKeys.push(result));
    await this._utilService.translate("EURO_MARGIN").then((result: any) => this.translatedKeys.push(result));
    await this._utilService.translate("PERCENTAGE_MARGIN").then((result: any) => this.translatedKeys.push(result));
    await this._utilService.translate("MONTH").then((result: any) => this.translatedKeys.push(result));
    await this._utilService.translate("YEAR").then((result: any) => this.translatedKeys.push(result));
  }

  loadBalance(updateURL: boolean = false) {
    this._utilService.showLoading();
    const oldValueShowChart = Boolean(this.showChart);
    this.showChart = false;
    const start_date = `${this.filterYearFrom}-${this.filterMonthFrom.toString().padStart(2, "0")}-01`;
    const end_date = moment().set("year", this.filterYearTo).set("month", this.filterMonthTo - 1).endOf("month").format("YYYY-MM-DD");
    this.http.get<any>(`/economic-balance/${start_date}/${end_date}`)
      .pipe(
        tap((results: any) => {
          results = (results || []).map((result: monthBalance) => {
            result.total_commission_real_cost = Number(result.total_commission_real_cost);
            result.total_provider_cost = Number(result.total_provider_cost);
            result.total_structure_cost = Number(result.total_structure_cost);
            result.total_profit = Number(result.total_profit);
            result.total_cost = Number(result.total_cost);
            result.margin_currency = Number(result.margin_currency);
            result.margin_percentage = Number(result.margin_percentage);
            return result;
          });

          this.total_row = {};

          if (results.length > 0) {

            for (const key of Object.keys(results[0]).slice(2)) {
              this.total_row[key] = results.map((result: monthBalance) => result[key]).reduce((a, b) => a + b, 0);
              this.total_row[key] = Number(this.total_row[key].toFixed(2));
            }
            this.total_row.margin_percentage /= results.length;
            this.total_row.margin_percentage = Number(this.total_row.margin_percentage.toFixed(2));

            this.chartData = [];

            for (let i = this.filterYearFrom; i <= this.filterYearTo; i++) {
              if (this.chartData.findIndex(item => item.year === i) >= 0) continue;
              const balanceForYear = this.fillMissingMonths(results.filter(item => item.year === i));
              this.chartData.push({
                year: i,
                series: [
                  {
                    name: this.translatedKeys[0],
                    group: "column1",
                    data: balanceForYear.map(item => item.total_profit)
                  },
                  {
                    name: this.translatedKeys[1],
                    group: "column2",
                    data: balanceForYear.map(item => item.total_commission_real_cost)
                  },
                  {
                    name: this.translatedKeys[2],
                    group: "column2",
                    data: balanceForYear.map(item => item.total_provider_cost)
                  },
                  {
                    name: this.translatedKeys[3],
                    group: "column2",
                    data: balanceForYear.map(item => item.total_structure_cost)
                  }
                ]
              });
              this.openedYear.push(i);
            }
            this.chartData = this.chartData.sort((a, b) => b.year - a.year);
          }
          this.balance.next(results);
          return results;
        })
      )
      .subscribe(() => {
        if (updateURL) {
          this.saveDatesInUrl(start_date, end_date);
        }
        setTimeout(() => {
          this.showChart = oldValueShowChart;
          this._changeDetectorRef.detectChanges();
          this._utilService.hideLoading();
        });
      });
  }

  saveDatesInUrl(start_date: string, end_date: string) {
    const url: string = window.location.origin + window.location.pathname + `?start_date=${start_date}&end_date=${end_date}`;
    window.history.replaceState({}, "", url);
  }

  fillMissingMonths(data: monthBalance[]) {
    if (!data || !data[0] || data[0].month === 1) return data;

    for (let i = 1; i < data[0].month; i++) {
      data.push({
        month: i,
        year: data[0].year,
        total_commission_real_cost: 0,
        total_provider_cost: 0,
        total_structure_cost: 0,
        total_profit: 0,
        total_cost: 0,
        margin_currency: 0,
        margin_percentage: 0,
        fake: true
      });
    }

    return data.sort((a, b) => a.month - b.month);
  }

  toggleOpenedYear(year: number) {
    this.openedYear = this.openedYear.includes(year) ? this.openedYear.filter(item => item !== year) : [...this.openedYear, year];
    this._changeDetectorRef.detectChanges();
  }

  yearIsOpen(year: number) {
    return this.openedYear.includes(year);
  }

  export() {
    this._utilService.showLoading();

    const data = this.balance.value.map((item: monthBalance) => {
      let obj: any = {};

      obj[this.translatedKeys[7]] = this.monthsList[item.month - 1];
      obj[this.translatedKeys[8]] = item.year;
      obj[this.translatedKeys[0]] = item.total_profit
      obj[this.translatedKeys[4]] = item.total_cost
      obj[this.translatedKeys[1]] = item.total_commission_real_cost
      obj[this.translatedKeys[2]] = item.total_provider_cost
      obj[this.translatedKeys[3]] = item.total_structure_cost
      obj[this.translatedKeys[5]] = item.margin_currency
      obj[this.translatedKeys[6]] = item.margin_percentage

      return obj;
    });

    this._utilService.exportAsExcelFile(data, 'export-economic-balance');

    this._utilService.hideLoading();
  }
}
